import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader, Btn, Card, Badge, Select, Spinner, useToast } from '../../components/shared/UI';
import api from '../../utils/api';
import { CASE_STATUS_COLORS, CASE_PRIORITY_COLORS } from '../Support';
import { CaseThread } from '../SupportCaseView';

const STATUS_LABELS = {
  open: 'Open', in_progress: 'In progress', waiting_on_user: 'Waiting on user',
  resolved: 'Resolved', closed: 'Closed',
};

export default function AdminSupportCaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { show, ToastContainer } = useToast();
  const [sc, setSc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [internal, setInternal] = useState(false);
  const [replyStatus, setReplyStatus] = useState('waiting_on_user');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/admin/support/${id}`).then(r => setSc(r.data))
      .catch(() => navigate('/admin/support'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const send = async e => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post(`/admin/support/${id}/messages`,
        { body: reply, internal, status: internal ? undefined : replyStatus });
      setSc(data); setReply(''); setInternal(false);
      show(internal ? 'Internal note added' : 'Reply sent — user notified');
    } catch (err) {
      show(err.response?.data?.message || 'Send failed', 'error');
    } finally { setSending(false); }
  };

  const patch = async fields => {
    try {
      const { data } = await api.patch(`/admin/support/${id}`, fields);
      setSc(data);
      show('Case updated');
    } catch (err) {
      show(err.response?.data?.message || 'Update failed', 'error');
    }
  };

  if (loading || !sc) return <Spinner />;
  const u = sc.user || {};

  return (
    <div>
      <ToastContainer />
      <PageHeader
        title={sc.subject}
        subtitle={`${sc.caseNumber} · ${sc.category} · opened ${new Date(sc.createdAt).toLocaleString()}`}
        actions={<Btn size="sm" variant="ghost" onClick={() => navigate('/admin/support')}>← All cases</Btn>} />

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 260px', gap: 20, alignItems: 'start' }}>
        <div>
          <Card style={{ marginBottom: 16 }}>
            <CaseThread messages={sc.messages || []} viewer="admin" meLabel="You" themLabel={u.name || 'User'} />
          </Card>

          <Card>
            <form onSubmit={send} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <textarea value={reply} onChange={e => setReply(e.target.value)} rows={3} required
                placeholder={internal ? 'Internal note (user will not see this)…' : 'Reply to the user…'}
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 13, fontFamily: 'inherit',
                  border: `1px solid ${internal ? 'rgba(245,158,11,0.5)' : 'var(--border)'}`,
                  borderRadius: 8, resize: 'vertical',
                  background: internal ? 'rgba(245,158,11,0.06)' : 'var(--input-bg)',
                  color: 'var(--text)', outline: 'none',
                }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={internal} onChange={e => setInternal(e.target.checked)} />
                  Internal note
                </label>
                {!internal && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)' }}>
                    then set status:
                    <select value={replyStatus} onChange={e => setReplyStatus(e.target.value)}
                      style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--input-bg)', color: 'var(--text)', fontSize: 13 }}>
                      {Object.keys(STATUS_LABELS).map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                    </select>
                  </label>
                )}
                <div style={{ flex: 1 }} />
                <Btn variant="primary" type="submit" disabled={sending}>
                  {sending ? 'Sending…' : internal ? 'Add note' : 'Send reply'}
                </Btn>
              </div>
            </form>
          </Card>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card>
            <div style={sideLabel}>Status</div>
            <div style={{ marginBottom: 10 }}><Badge label={STATUS_LABELS[sc.status]} color={CASE_STATUS_COLORS[sc.status]} /></div>
            <Select value={sc.status} onChange={e => patch({ status: e.target.value })}>
              {Object.keys(STATUS_LABELS).map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </Select>
            <div style={{ ...sideLabel, marginTop: 14 }}>Priority</div>
            <Select value={sc.priority} onChange={e => patch({ priority: e.target.value })}>
              {['low', 'normal', 'high'].map(p => <option key={p} value={p}>{p}</option>)}
            </Select>
          </Card>

          <Card>
            <div style={sideLabel}>User</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{u.name || '—'}</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', wordBreak: 'break-all' }}>{u.email}</div>
            <div style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 8 }}>
              Plan: <b style={{ color: 'var(--text-muted)' }}>{u.plan || '—'}</b> · {u.subscriptionStatus || '—'}
              {u.createdAt && <><br />Registered {new Date(u.createdAt).toLocaleDateString()}</>}
            </div>
            <div style={{ marginTop: 10 }}>
              <Btn size="sm" variant="ghost" onClick={() => navigate('/admin/users')}>Manage users →</Btn>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

const sideLabel = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-subtle)', marginBottom: 6 };
